import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Chip, Autocomplete, TextField } from '@mui/material';
import { getColorForCategory } from '../../../../utils/categoryColors';

const CategoriaChips = ({ 
  categorias = [],
  selectedCategorias = [],
  setSelectedCategorias,
  allLabel = 'Todas las categorías',
}) => {
  const todasCategorias = useMemo(() => ({
    label: allLabel,
    id: 'all_categories',
  }), [allLabel]);

  const opciones = useMemo(() => ([
    todasCategorias,
    ...categorias.map((cat) => ({ label: cat.nombre, id: cat.id })),
  ]), [categorias, todasCategorias]);

  const handleChange = (event, value) => {
    const hasAll = value.some((item) => item.id === 'all_categories');

    if (hasAll) {
      if (selectedCategorias.length === categorias.length) {
        setSelectedCategorias([]);
      } else {
        setSelectedCategorias(categorias.map((cat) => cat.id));
      }
      return;
    }
    setSelectedCategorias(value.map((item) => item.id));
  };

  const renderTags = (value, getTagProps) => {
    const isAllSelected = selectedCategorias.length === categorias.length;

    if (isAllSelected) {
      return (
        <Chip
          label={allLabel}
          {...getTagProps({ index: 0 })}
          key="all_categories"
          color="primary"
        />
      );
    }

    return value.map((option, index) => (
      <Chip
        label={option.label}
        {...getTagProps({ index })}
        key={option.id}
        color={getColorForCategory(option.label)}
      />
    ));
  };

  const getOptionStyle = (selected) => ({
    backgroundColor: selected ? 'rgba(25,118,210,0.08)' : 'transparent',
    fontWeight: selected ? 600 : 400,
    padding: '8px 16px',
  });

  const renderOption = (props, option, { selected }) => (
    <li
      {...props}
      key={option.id}
      style={{ ...props.style, ...getOptionStyle(selected) }}
    >
      <span style={{ color: selected ? '#1976d2' : 'inherit' }}>
        {option.label}
      </span>
    </li>
  );

  return (
    <Autocomplete
      multiple
      options={opciones}
      getOptionLabel={(option) => option.label}
      value={
        selectedCategorias.length === categorias.length
          ? [todasCategorias]
          : opciones.filter((o) => selectedCategorias.includes(o.id))
      }
      onChange={handleChange}
      renderTags={renderTags}
      renderOption={renderOption}
      disableCloseOnSelect
      // ⬇️ mantener seleccionados en la lista (no ocultarlos)
      filterSelectedOptions={false}
      // ⬇️ comparar por id
      isOptionEqualToValue={(option, value) => option.id === value.id}
      // ⬇️ sin escritura, solo selección con mouse/touch
      openOnFocus
      selectOnFocus
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          size="small"
          placeholder="Seleccionar Categorías"
          inputProps={{
            ...params.inputProps,
            readOnly: true,      // bloquea la escritura
          }}
          onKeyDown={(e) => {
            // bloquea tecleo en el input (se puede quitar chips con la X)
            e.preventDefault();
          }}
        />
      )}
      sx={{ width: '100%' }}
    />
  );
};

CategoriaChips.propTypes = {
  categorias: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      nombre: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedCategorias: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedCategorias: PropTypes.func.isRequired,
  allLabel: PropTypes.string,
};

CategoriaChips.defaultProps = {
  allLabel: 'Todas las categorías',
};

export default CategoriaChips;
